// Phase RV-3 — useRolloverTracker hook.
//
// Anon-readable Supabase reader against rollover_pipeline_runs +
// rollover_slips + rollover_candidates + rollover_win_rates.
//
// Selection Tracker spec §05: shape returns the last N cycles
// (default 12) for either the active user chain (when authenticated
// with a chain) or system-level (when no chain).
//
// Doctrine carry-forward:
//   - No service-role; anon JWT only. RLS already permits anon SELECT
//     for the last 30 days of candidates + published/settled slips +
//     win_rates (per RV-1).
//   - Never fabricate. NULL combined_odds stays NULL; skip cycles
//     surface as status='skipped' with no leg count gymnastics.
//   - N06: actual_win_rate is only meaningful when resolved_slips_count
//     ≥ 50. The hook returns the raw figures; the UI applies the gate.
//   - N07: never delete rollover_* rows. Read-only hook.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export type RolloverTier = "2x" | "3x" | "4x" | "5x" | "6x";
export type WindowLabel = "midweek" | "weekend" | "supplement";
export type SlipStatus =
  | "building"
  | "assembled"
  | "published"
  | "settled"
  | "skipped"
  | "rebalance_required";
export type SlipOutcome = "won" | "lost" | "void" | "skipped" | "pending";
export type PoolTag = "selected" | "evaluated" | "assessed";
export type FilterStage =
  | "stage_1_scan"
  | "stage_2_form_value"
  | "stage_3_odds"
  | "stage_5_adversarial";

export interface TrackerCandidate {
  candidate_id: string;
  match_name: string;
  league: string | null;
  kickoff_at: string | null;
  market: string | null;
  pick: string | null;
  odds: number | null;
  form_score: number | null;
  value_edge_pct: number | null;
  confidence_score: number | null;
  clv_edge_detected: boolean;
  pool_tag: PoolTag;
  discard_reason: string | null;
  agent_reasoning: string | null;
  filter_stage_failed: FilterStage | null;
}

export interface TrackerSlip {
  slip_id: string;
  status: SlipStatus;
  combined_odds: number | null;
  leg_count: number;
  avg_confidence: number | null;
  outcome: SlipOutcome | null;
  selections: unknown;
  adversarial_log: unknown;
  settled_at: string | null;
  published_at: string | null;
}

export interface TrackerCycle {
  run_id: string;
  tier: RolloverTier;
  window_label: WindowLabel;
  started_at: string;
  completed_at: string | null;
  slip: TrackerSlip | null;
  candidates: {
    selected: TrackerCandidate[];
    evaluated: TrackerCandidate[];
    assessed: TrackerCandidate[];
    total_scanned: number;
    rejection_breakdown: Record<string, number>;
  };
}

export interface TrackerWinRate {
  seeded_default: number;
  actual_win_rate: number | null;
  resolved_slips_count: number;
  kelly_uses_actual: boolean;
}

export interface TrackerActiveChain {
  id: string;
  tier: RolloverTier | null;
  window_config: WindowLabel | null;
  current_week: number;
  status: string;
}

export interface RolloverTrackerState {
  activeChain: TrackerActiveChain | null;
  cycles: TrackerCycle[];
  winRates: Record<RolloverTier, TrackerWinRate>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const TIERS: readonly RolloverTier[] = ["2x", "3x", "4x", "5x", "6x"];

function asTier(v: unknown): RolloverTier | null {
  return v === "2x" || v === "3x" || v === "4x" || v === "5x" || v === "6x" ? v : null;
}
function asWindow(v: unknown): WindowLabel | null {
  return v === "midweek" || v === "weekend" || v === "supplement" ? v : null;
}
function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function asPool(v: unknown): PoolTag | null {
  return v === "selected" || v === "evaluated" || v === "assessed" ? v : null;
}
function asFilterStage(v: unknown): FilterStage | null {
  return v === "stage_1_scan" ||
    v === "stage_2_form_value" ||
    v === "stage_3_odds" ||
    v === "stage_5_adversarial"
    ? v
    : null;
}
function asSlipStatus(v: unknown): SlipStatus {
  return v === "building" ||
    v === "assembled" ||
    v === "published" ||
    v === "settled" ||
    v === "skipped" ||
    v === "rebalance_required"
    ? v
    : "building";
}
function asOutcome(v: unknown): SlipOutcome | null {
  return v === "won" ||
    v === "lost" ||
    v === "void" ||
    v === "skipped" ||
    v === "pending"
    ? v
    : null;
}

function normCandidate(raw: Record<string, unknown>): TrackerCandidate | null {
  const pool = asPool(raw.pool_tag);
  if (!pool) return null;
  const id = asStr(raw.candidate_id);
  const name = asStr(raw.match_name);
  if (!id || !name) return null;
  return {
    candidate_id: id,
    match_name: name,
    league: asStr(raw.league),
    kickoff_at: asStr(raw.kickoff_at),
    market: asStr(raw.market),
    pick: asStr(raw.pick),
    odds: asNumOrNull(raw.odds),
    form_score: asNumOrNull(raw.form_score),
    value_edge_pct: asNumOrNull(raw.value_edge_pct),
    confidence_score: asNumOrNull(raw.confidence_score),
    clv_edge_detected: raw.clv_edge_detected === true,
    pool_tag: pool,
    discard_reason: asStr(raw.discard_reason),
    agent_reasoning: asStr(raw.agent_reasoning),
    filter_stage_failed: asFilterStage(raw.filter_stage_failed),
  };
}

function normSlip(raw: Record<string, unknown> | undefined): TrackerSlip | null {
  if (!raw) return null;
  const id = asStr(raw.slip_id);
  if (!id) return null;
  return {
    slip_id: id,
    status: asSlipStatus(raw.status),
    combined_odds: asNumOrNull(raw.combined_odds),
    leg_count: typeof raw.leg_count === "number" ? raw.leg_count : 0,
    avg_confidence: asNumOrNull(raw.avg_confidence),
    outcome: asOutcome(raw.outcome),
    selections: raw.selections ?? null,
    adversarial_log: raw.adversarial_log ?? null,
    settled_at: asStr(raw.settled_at),
    published_at: asStr(raw.published_at),
  };
}

export interface UseRolloverTrackerOptions {
  /** Override the max cycles returned. Defaults to 12. */
  limit?: number;
  /** Override the chain id (e.g. archive deep-link). */
  chainId?: string | null;
}

export function useRolloverTracker(
  opts: UseRolloverTrackerOptions = {},
): RolloverTrackerState {
  const { limit = 12, chainId = null } = opts;
  const supabase = useMemo(() => getBrowserClient(), []);
  const [activeChain, setActiveChain] = useState<TrackerActiveChain | null>(null);
  const [cycles, setCycles] = useState<TrackerCycle[]>([]);
  const [winRates, setWinRates] = useState<Record<RolloverTier, TrackerWinRate>>(
    () => ({
      "2x": { seeded_default: 0.68, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
      "3x": { seeded_default: 0.55, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
      "4x": { seeded_default: 0.48, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
      "5x": { seeded_default: 0.41, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
      "6x": { seeded_default: 0.36, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
    }),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;

      // 1) Win rates — seeded table is anon-readable.
      const wr = await sb
        .from("rollover_win_rates")
        .select("tier, seeded_default, actual_win_rate, resolved_slips_count, kelly_uses_actual");
      if (wr.error) throw new Error(wr.error.message);
      const wrMap: Record<RolloverTier, TrackerWinRate> = {
        "2x": { seeded_default: 0.68, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
        "3x": { seeded_default: 0.55, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
        "4x": { seeded_default: 0.48, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
        "5x": { seeded_default: 0.41, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
        "6x": { seeded_default: 0.36, actual_win_rate: null, resolved_slips_count: 0, kelly_uses_actual: false },
      };
      for (const row of (wr.data ?? []) as Array<Record<string, unknown>>) {
        const t = asTier(row.tier);
        if (!t) continue;
        wrMap[t] = {
          seeded_default: asNumOrNull(row.seeded_default) ?? wrMap[t].seeded_default,
          actual_win_rate: asNumOrNull(row.actual_win_rate),
          resolved_slips_count:
            typeof row.resolved_slips_count === "number" ? row.resolved_slips_count : 0,
          kelly_uses_actual: row.kelly_uses_actual === true,
        };
      }
      setWinRates(wrMap);

      // 2) Active chain (if any).
      let chainRow: TrackerActiveChain | null = null;
      if (chainId) {
        const c = await sb
          .from("rollover_chains")
          .select("id, current_week, status")
          .eq("id", chainId)
          .maybeSingle();
        if (!c.error && c.data) {
          chainRow = {
            id: String(c.data.id),
            tier: null,
            window_config: null,
            current_week: typeof c.data.current_week === "number" ? c.data.current_week : 1,
            status: typeof c.data.status === "string" ? c.data.status : "active",
          };
        }
      } else {
        const c = await sb
          .from("rollover_chains")
          .select("id, current_week, status, created_at")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(1);
        if (!c.error && Array.isArray(c.data) && c.data.length > 0) {
          const r = c.data[0] as Record<string, unknown>;
          chainRow = {
            id: String(r.id),
            tier: null,
            window_config: null,
            current_week: typeof r.current_week === "number" ? r.current_week : 1,
            status: typeof r.status === "string" ? r.status : "active",
          };
        }
      }
      setActiveChain(chainRow);

      // 3) Pipeline runs (last N).
      const runsQuery = sb
        .from("rollover_pipeline_runs")
        .select("run_id, tier, window_label, started_at, completed_at, outcome")
        .order("started_at", { ascending: false })
        .limit(limit);
      const runsRes = await runsQuery;
      if (runsRes.error) throw new Error(runsRes.error.message);
      const runs = (runsRes.data ?? []) as Array<Record<string, unknown>>;

      if (runs.length === 0) {
        setCycles([]);
        setLoading(false);
        return;
      }

      const runIds = runs
        .map((r) => asStr(r.run_id))
        .filter((s): s is string => s !== null);

      // 4) Slips for those runs.
      const slipsRes = await sb
        .from("rollover_slips")
        .select(
          "slip_id, pipeline_run_id, status, combined_odds, leg_count, avg_confidence, outcome, selections, adversarial_log, settled_at, published_at",
        )
        .in("pipeline_run_id", runIds);
      const slipsByRun = new Map<string, TrackerSlip>();
      if (!slipsRes.error) {
        for (const s of (slipsRes.data ?? []) as Array<Record<string, unknown>>) {
          const rid = asStr(s.pipeline_run_id);
          const norm = normSlip(s);
          if (rid && norm) slipsByRun.set(rid, norm);
        }
      }

      // 5) Candidates for those runs.
      const candsRes = await sb
        .from("rollover_candidates")
        .select(
          "candidate_id, pipeline_run_id, match_name, league, kickoff_at, market, pick, odds, form_score, value_edge_pct, confidence_score, clv_edge_detected, pool_tag, discard_reason, agent_reasoning, filter_stage_failed",
        )
        .in("pipeline_run_id", runIds);
      const candsByRun = new Map<string, TrackerCandidate[]>();
      if (!candsRes.error) {
        for (const c of (candsRes.data ?? []) as Array<Record<string, unknown>>) {
          const rid = asStr(c.pipeline_run_id);
          if (!rid) continue;
          const norm = normCandidate(c);
          if (!norm) continue;
          const arr = candsByRun.get(rid) ?? [];
          arr.push(norm);
          candsByRun.set(rid, arr);
        }
      }

      const built: TrackerCycle[] = [];
      for (const r of runs) {
        const run_id = asStr(r.run_id);
        const tier = asTier(r.tier);
        const window_label = asWindow(r.window_label);
        const started_at = asStr(r.started_at);
        if (!run_id || !tier || !window_label || !started_at) continue;
        const cands = candsByRun.get(run_id) ?? [];
        const selected = cands.filter((c) => c.pool_tag === "selected");
        const evaluated = cands.filter((c) => c.pool_tag === "evaluated");
        const assessed = cands.filter((c) => c.pool_tag === "assessed");
        const rejection_breakdown: Record<string, number> = {};
        for (const c of cands) {
          if (c.filter_stage_failed) {
            rejection_breakdown[c.filter_stage_failed] =
              (rejection_breakdown[c.filter_stage_failed] ?? 0) + 1;
          }
        }
        built.push({
          run_id,
          tier,
          window_label,
          started_at,
          completed_at: asStr(r.completed_at),
          slip: slipsByRun.get(run_id) ?? null,
          candidates: {
            selected,
            evaluated,
            assessed,
            total_scanned: cands.length,
            rejection_breakdown,
          },
        });
      }
      setCycles(built);
      setLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Tracker query failed");
      setLoading(false);
    }
  }, [supabase, limit, chainId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<RolloverTrackerState>(
    () => ({ activeChain, cycles, winRates, loading, error, refetch }),
    [activeChain, cycles, winRates, loading, error, refetch],
  );
}

export { TIERS as ROLLOVER_TIERS };
